import { useEffect, useMemo, useRef, useState } from "react";
import { Eye, Flame, Clock, ShoppingBag, Hourglass } from "lucide-react";

// Deterministic pseudo-random per product id (stable across reloads)
function seedFromId(id) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function pickStyle(seed) {
  return seed % 4; // 0..3
}

function fmtCountdown(ms) {
  if (ms <= 0) return "00:00:00";
  const h = String(Math.floor(ms / 3_600_000)).padStart(2, "0");
  const m = String(Math.floor((ms % 3_600_000) / 60_000)).padStart(2, "0");
  const s = String(Math.floor((ms % 60_000) / 1000)).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

export default function UrgencyBadge({ id, lang = "id" }) {
  const seed = useMemo(() => seedFromId(String(id || Math.random())), [id]);
  const style = pickStyle(seed);
  const [tick, setTick] = useState(0);

  // Refresh every 4-7 seconds for "live" feel
  useEffect(() => {
    const ms = 4000 + (seed % 3000);
    const t = setInterval(() => setTick((v) => v + 1), ms);
    return () => clearInterval(t);
  }, [seed]);

  // Stable countdown end time per product (between 2h-23h ahead)
  const countdownEnd = useRef(Date.now() + ((seed % 21) + 2) * 3_600_000);

  if (style === 0) {
    // Live viewers — drifts slightly
    const base = 12 + (seed % 38); // 12..49
    const drift = ((seed + tick * 7) % 6) - 2; // -2..+3
    const viewers = base + drift;
    return (
      <Badge color="orange" icon={Eye}>
        {lang === "id" ? `${viewers} orang melihat` : `${viewers} viewing now`}
      </Badge>
    );
  }
  if (style === 1) {
    // Stock left — slowly decreasing
    const initial = 8 + (seed % 14); // 8..21
    const sold = Math.min(initial - 2, Math.floor(tick / 4)); // never reach 0
    const left = Math.max(2, initial - sold);
    return (
      <Badge color="red" icon={Flame}>
        {lang === "id" ? `Tinggal ${left} unit!` : `Only ${left} left!`}
      </Badge>
    );
  }
  if (style === 2) {
    // Recent sales
    const sales = 24 + (seed % 76); // 24..99
    return (
      <Badge color="green" icon={ShoppingBag}>
        {lang === "id" ? `Terjual ${sales}× minggu ini` : `Sold ${sales}× this week`}
      </Badge>
    );
  }
  // style === 3: countdown
  const remaining = countdownEnd.current - Date.now();
  return (
    <Badge color="ink" icon={Hourglass}>
      {lang === "id" ? `Promo: ${fmtCountdown(remaining)}` : `Ends in: ${fmtCountdown(remaining)}`}
    </Badge>
  );
}

function Badge({ color, icon: Icon, children }) {
  const cls = {
    orange: "bg-brand-orange/15 text-brand-orange",
    red: "bg-brand-red/10 text-brand-red",
    green: "bg-green-100 text-green-700",
    ink: "bg-ink/90 text-white",
  }[color] || "bg-accent-subtle text-brand-red";
  return (
    <div
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold tracking-wide ${cls}`}
      data-testid="urgency-badge"
    >
      <Icon className="w-3 h-3" />
      <span>{children}</span>
    </div>
  );
}
